// React
import { gql, useLazyQuery, useQuery, useMutation } from "@apollo/client";
import PropTypes from "prop-types";
import { useEffect, useState, useContext } from "react";

// @mui material components
import {
	Card,
	CircularProgress,
	Fade,
	Grid,
	Icon,
	InputLabel,
	Menu,
	MenuItem,
	Modal,
	Skeleton,
	Switch,
	Table,
	TableBody,
	TableContainer,
	TableRow,
	Tooltip,
} from "@mui/material";

// Custom UI components
import borders from "assets/theme/base/borders";
import colors from "assets/theme/base/colors";
import typography from "assets/theme/base/typography";
import SinDatos from "components/Carteles/SinDatos";
import SinResultados from "components/Carteles/SinResultados";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftInput from "components/SoftInput";
import SoftTypography from "components/SoftTypography";
import TableItem from "../TableItem";
import { MessageManager } from "context";
import ModalProducto from "../../ModalProducto";
import { getUproColor } from "utils/colors";
import { formatMoneyPunto } from "utils/formatters";
import { ConfirmarContext } from "context/ConfirmarContext";
import { formatDate } from "utils/formatters";
import ModalSelectProvision from "../ModalSelectProvision";
import SoftBadge from "components/SoftBadge";
import { nombreCompraDetalle } from "utils/presentacionCompra";
import ModalCambioProducto from "../ModalCambioProducto";
import ModalRemitos from "../ModalRemitos";
import {
	computeDeliveryPercentage,
	computePendingQuantity,
	toBaseQuantity,
} from "utils/provisionQuantity";

const DELIVERY_PERCENTAGE_EPSILON = 1e-6;

function OverdeliveryWarning({ rows }) {
	const headers = ["Materia prima", "Cantidad pendiente", "Cantidad ingresada", "% acumulado al guardar"];

	return (
		<SoftBox width="100%">
			<SoftTypography component="p" variant="button" color="dark" fontWeight="medium">
				Se detectaron cantidades superiores a las esperadas según pedido. ¿Está seguro de continuar?
			</SoftTypography>
			<TableContainer
				sx={{
					mt: 2,
					maxHeight: "45vh",
					overflow: "auto",
					boxShadow: "none",
					border: "1px solid #E7EBF3",
					borderRadius: "10px",
				}}
			>
				<Table size="small" aria-label="Cantidades superiores a las esperadas" sx={{ minWidth: "620px" }}>
					<SoftBox component="thead">
						<TableRow>
							{headers.map((header) => (
								<SoftBox
									key={header}
									component="th"
									scope="col"
									p={1}
									textAlign={header === "Materia prima" ? "left" : "center"}
									whiteSpace="nowrap"
								>
									<SoftTypography variant="caption" color="secondary" fontWeight="bold">
										{header}
									</SoftTypography>
								</SoftBox>
							))}
						</TableRow>
					</SoftBox>
					<TableBody>
						{rows.map((row) => (
							<TableRow key={row.idProvisionDetalle}>
								<SoftBox component="td" p={1} textAlign="left">
									<SoftTypography variant="caption" color="secondary" fontWeight="medium">
										{row.nombre}
									</SoftTypography>
								</SoftBox>
								<SoftBox component="td" p={1} textAlign="center" whiteSpace="nowrap">
									<SoftTypography variant="caption" color="secondary" fontWeight="medium">
										{formatMoneyPunto(row.cantidadEsperada)} {row.unidad}
									</SoftTypography>
								</SoftBox>
								<SoftBox component="td" p={1} textAlign="center" whiteSpace="nowrap">
									<SoftTypography variant="caption" color="secondary" fontWeight="medium">
										{formatMoneyPunto(row.cantidadIngresada)} {row.unidad}
									</SoftTypography>
								</SoftBox>
								<SoftBox component="td" p={1} textAlign="center" whiteSpace="nowrap">
									<SoftTypography variant="caption" color="warning" fontWeight="bold">
										{formatMoneyPunto(row.porcentajeEntrega)}%
									</SoftTypography>
								</SoftBox>
							</TableRow>
						))}
					</TableBody>
				</Table>
			</TableContainer>
		</SoftBox>
	);
}

OverdeliveryWarning.propTypes = {
	rows: PropTypes.arrayOf(PropTypes.object).isRequired,
};

const PROVISION_TERMINAL_STATES = [
	"Entregada",
	"Cerrado con entrega parcial",
	"Completada con entrega parcial",
	"Completada sin entregas",
];

const style = {
	position: "absolute",
	top: "50%",
	left: "50%",
	transform: "translate(-50%, -50%)",
	maxHeight: "90%",
	width: { xs: "90%", sm: "80%", xxl: "70%" },
	overflow: "auto",
	p: 3,
};

const columnsBase = [
	{ name: "id", desc: "ID", compact: true, align: "center" },
	{ name: "nombre", desc: "Nombre", align: "left" },
	{ name: "mpDestino", desc: "MP Destino", align: "left" },
	{ name: "categoria", desc: "Rubro", align: "left" },
	{ name: "entregado", desc: "Recibido / solicitado", compact: true, align: "center" },
	{ name: "cantidad", desc: "Cantidad a ingresar", compact: true, minWidth: "108px", align: "center" },
	{ name: "porcentajeEntrega", desc: "% acumulado al guardar", compact: true, align: "center" },
	{ name: "unidad", desc: "Unidad", compact: true, align: "center" },
	{ name: "eliminar", desc: " ", compact: true, align: "center", noOrder: true },
];

const columnsSelect = [
	{ name: "id", desc: "ID", compact: true, align: "center" },
	{ name: "nombre", desc: "Nombre", align: "left" },
	{ name: "categoria", desc: "Rubro", align: "left" },
	{ name: "entregado", desc: "Recibido / solicitado", compact: true, align: "center" },
	{ name: "cantidad", desc: "Cantidad a ingresar", compact: true, minWidth: "108px", align: "center" },
	{ name: "porcentajeEntrega", desc: "% acumulado al guardar", compact: true, align: "center" },
	{ name: "unidad", desc: "Unidad", compact: true, align: "center" },
];

const INTEGER_EPSILON = 1e-6;
const toSafeInt = (value) => {
	const numericValue = Number(value) || 0;
	const roundedValue = Math.round(numericValue);
	if (Math.abs(numericValue - roundedValue) > INTEGER_EPSILON) {
		return null;
	}
	return roundedValue;
};

export default function ModalNuevoRemito({
	open,
	handleClose,
	selectedRemito,
	idSucursal,
	refetchProductos,
}) {
	// Context
	const { handleSnackbar } = useContext(MessageManager);
	const { confirmar } = useContext(ConfirmarContext);
	const [filterQuantity, setFilterQuantity] = useState(false);

	// States
	const [openMenu, setOpenMenu] = useState(false);
	const [productoToAdd, setProductoToAdd] = useState();
	const [anchorEl, setAnchorEl] = useState(null);
	const [searchTimeout, setSearchTimeout] = useState(null);
	const [productosRemito, setProductosRemito] = useState([]);
	const [openModalProducto, setOpenModalProducto] = useState(false);
	const [selectedItem, setSelectedItem] = useState();
	const [openAddMenu, setOpenAddMenu] = useState(false);
	const [remito, setRemito] = useState("");
	const [openSelectProvision, setOpenSelectProvision] = useState(false);
	const [selectedProvision, setSelectedProvision] = useState();
	const [cambioProductoData, setCambioProductoData] = useState(null);
	const [openRemitosModal, setOpenRemitosModal] = useState(false);
	const [materiasPrimaOriginalesPedido, setMateriasPrimaOriginalesPedido] = useState([]);

	// Other
	const { light } = colors;
	const { size, fontWeightBold } = typography;
	const { borderWidth } = borders;

	const [getProductos, { data: dataMP, loading }] = useLazyQuery(
		gql`
      query getProductos($page: Int, $order: Order, $filter: JSON) {
        paginationInfo {
          pages
          total
        }
        materiasPrimas(pagination: { page: $page, limit: 50 }, order: $order, filter: $filter) {
          id
          codigo
          tipo
          categoria
          nombre
          disabled
          precio
          tipoUnidad {
            id
            nombre
            abreviatura
            multiplicador
          }
        }
      }
    `,
		{
			fetchPolicy: "network-only",
		}
	);
	const [saveRemito, { loading: loadingSave }] = useMutation(
		gql`
      mutation saveMovimientoModalNuevoRemito($input: MovimientoInput!) {
        saveMovimiento(input: $input) {
          id
        }
      }
    `
	);
	const [getSucursal, { data: sucursal }] = useLazyQuery(
		gql`
      query getSucursal($id: ID!) {
        sucursal(id: $id) {
          id
          nombre
          sede {
            id
            nombre
          }
        }
      }
    `
	);

	// Traer detalles del pedido de compra con campos necesarios para conversión pack <-> unidad base
	const [getPedidoCompraDetalles] = useLazyQuery(
		gql`
	      query getPedidoCompraDetalles($id: ID) {
	        pedidosCompra(filter: { id: $id }) {
	          id
	          detalles {
	            id
	            idMateriaPrima
	            nombreCompra
	            requierePack
	            unidadPack
	            totalMP
	            cantidad
	            materiaPrima {
	              id
	              codigo
	              tipo
	              nombre
	              categoria
	              idMateriaPrimaReferencia
	              tipoUnidad {
	                id
	                nombre
	                abreviatura
	                multiplicador
	              }
	            }
	          }
	        }
	      }
    `,
		{ fetchPolicy: "network-only" }
	);

	// useEffects
	useEffect(() => {
		setRemito();
		setProductosRemito([]);
		setSelectedProvision();
		setMateriasPrimaOriginalesPedido([]);
			if (open && selectedRemito) {
				setSelectedProvision(selectedRemito.provision);

				let mpCargados = selectedRemito.provision?.detalles.map((item) => {
					const movimientoDetalle = selectedRemito.movimientoDetalles.find((det) => {
						return det.idProvisionDetalle === item.id;
					});
					return {
						...(movimientoDetalle?.materiaPrima ?? item.materiaPrimaRecepcion ?? item.materiaPrima),
						mpOriginal: item.materiaPrima,
						idMateriaPrima: (movimientoDetalle?.materiaPrima ?? item.materiaPrimaRecepcion ?? item.materiaPrima)?.id,
						idMateriaPrimaDestino: (movimientoDetalle?.materiaPrima ?? item.materiaPrimaRecepcion ?? item.materiaPrima)?.id,
						materiaPrimaDestino:
							movimientoDetalle?.materiaPrima ?? item.materiaPrimaRecepcion ?? item.materiaPrima,
						idProvisionDetalle: null,
						cantidad: movimientoDetalle?.cantidad,
						cantidadEntregada: item?.cantidadEntregada ?? 0,
						cantidadRequerida: item?.cantidad ?? 0,
						precioUnitarioOriginal: item?.precioUnitario,
						enabled: true,
						opcion: 1
					};
				});
			setProductosRemito(mpCargados);
		} else {
			setRemito();
			setProductosRemito([]);
			setSelectedProvision();
			setMateriasPrimaOriginalesPedido([]);
		}
	}, [open]);

	useEffect(() => {
		if (productoToAdd && dataMP?.materiasPrimas?.length > 0) {
			if (dataMP.materiasPrimas.find((producto) => producto.id === productoToAdd)) {
				handleAddProduct(dataMP.materiasPrimas.find((mp) => mp.id === productoToAdd));
			}
			setProductoToAdd();
		}
	}, [dataMP]);

	useEffect(() => {
		if (idSucursal && open) getSucursal({ variables: { id: idSucursal } });
	}, [idSucursal, open]);

	// Handlers
	const handleChanges = (event) => {
		setRemito({ ...remito, [event.target.name]: event.target.value });
	};

	const handleOpenAddMenu = (event) => {
		setAnchorEl(event.currentTarget);
		setOpenAddMenu(true);
	};

	const handleCloseAddMenu = () => {
		setAnchorEl(null);
		setOpenAddMenu(false);
	};

	const handleCloseMenu = () => {
		setAnchorEl(null);
		setOpenMenu(false);
	};

	const handleUpdateItem = (item, index) => {
		// Actualiza una sola fila: prioriza idProvisionDetalle cuando exista; de lo contrario usa el índice
		setProductosRemito((prev) =>
			prev.map((prevItem, i) => {
				const isSameRow =
					item?.idProvisionDetalle != null
						? prevItem?.idProvisionDetalle === item.idProvisionDetalle
						: i === index;
				return isSameRow ? item : prevItem;
			})
		);
	};

	const handleAddProduct = (product) => {
		setProductosRemito([...productosRemito, product]);
	};

	const renderColumnsBase = columnsBase.map(({ name, desc, align, compact, minWidth }) => {
		return (
			<SoftBox
				key={name}
				component="th"
				scope="col"
				width={compact ? "1%" : "auto"}
				minWidth={minWidth}
				pt={1.5}
				pb={1.25}
				px={1}
				textAlign={align}
				whiteSpace="nowrap"
				fontSize={size.xxs}
				fontWeight={fontWeightBold}
				color="secondary"
				opacity={0.7}
				borderBottom={`${borderWidth[1]} solid ${light.main}`}
			>
				{name === "mpDestino" ? (
					<Tooltip title="Materia prima de destino" placement="top">
						<span>{desc ? desc.toUpperCase() : name.toUpperCase()}</span>
					</Tooltip>
				) : (
					desc ? desc.toUpperCase() : name.toUpperCase()
				)}
			</SoftBox>
			);
		});

	const renderColumnsSelect = columnsSelect.map(({ name, desc, align, compact, minWidth }) => {
		return (
			<SoftBox
				key={name}
				component="th"
				scope="col"
				width={compact ? "1%" : "auto"}
				minWidth={minWidth}
				pt={1.5}
				pb={1.25}
				px={1}
				textAlign={align}
				whiteSpace="nowrap"
				fontSize={size.xxs}
				fontWeight={fontWeightBold}
				color="secondary"
				opacity={0.7}
				borderBottom={`${borderWidth[1]} solid ${light.main}`}
			>
				{desc ? desc.toUpperCase() : name.toUpperCase()}
			</SoftBox>
		);
	});

	const persistRemito = (input) => {
		saveRemito({ variables: { input } })
			.then(() => {
				handleSnackbar("El remito se ha guardado correctamente", "success");
				handleClose();
				refetchProductos();
			})
			.catch(() => {
				handleSnackbar("Ha ocurrido un error al guardar el remito", "error");
			});
	};

	const handleSave = () => {
		const filasSinDestino = productosRemito.filter((item) => !item?.idMateriaPrimaDestino);
		if (filasSinDestino.length > 0) {
			const pendientesAlternativas = filasSinDestino.filter(
				(item) => item?.idMateriaPrimaReferencia != null
			);
			if (pendientesAlternativas.length > 0) {
				handleSnackbar(
					`Falta reemplazar: ${pendientesAlternativas
						.map((item) => `ID ${item?.id} - ${item?.nombre}`)
						.join(", ")}`,
					"error"
				);
			} else {
				handleSnackbar("Hay filas sin materia prima de destino.", "error");
			}
			return;
		}

		const movimientoDetalle = [];
		const cantidadesSuperiores = [];
		for (const item of productosRemito.filter((detail) => detail.enabled)) {
			if (!item?.idProvisionDetalle) {
				handleSnackbar(
					`El producto ${item?.nombre || item?.id || ""} no está vinculado a un detalle de OP.`,
					"error"
				);
				return;
			}

			if (!item?.idMateriaPrimaDestino) {
				handleSnackbar(
					`El producto ${item?.nombre || item?.id || ""} no tiene materia prima de destino definida.`,
					"error"
				);
				return;
			}

			const cantidadBase = toBaseQuantity({
				quantityOriginal: item.cantidad,
				multiplicador: item?.tipoUnidad?.multiplicador,
				pedidoCompraDetalle: {
					requierePack: item?.requierePack,
					unidadPack: item?.unidadPack,
					totalMP: item?.totalMP,
					cantidad: item?.cantidadPedidoCompra,
				},
			});

			const cantidadInt = toSafeInt(cantidadBase);
			if (cantidadInt === null) {
				handleSnackbar(
					`La cantidad para ${item?.nombre || `MP #${item?.id}`} no es un entero valido (${cantidadBase}).`,
					"error"
				);
				return;
			}

			movimientoDetalle.push({
				idMateriaPrima: item.idMateriaPrimaDestino,
				cantidad: cantidadInt,
				idSucursal: idSucursal,
				precio:
					item.nuevoPrecio ??
					item.precioUnitarioOriginal ??
					item.precio ??
					0,
				idProvisionDetalle: item.idProvisionDetalle,
			});

			const porcentajeEntrega = computeDeliveryPercentage({
				delivered: item.cantidadEntregada,
				incoming: item.cantidad,
				required: item.cantidadRequerida,
			});
			if (porcentajeEntrega > 100 + DELIVERY_PERCENTAGE_EPSILON) {
				cantidadesSuperiores.push({
					idProvisionDetalle: item.idProvisionDetalle,
					nombre:
						item?.requierePack && item?.unidadPack
							? item?.nombreCompra || item?.nombre
							: item?.nombre,
					cantidadEsperada: computePendingQuantity({
						requiredOriginal: item.cantidadRequerida,
						deliveredOriginal: item.cantidadEntregada,
					}),
					cantidadIngresada: Number(item.cantidad) || 0,
					porcentajeEntrega,
					unidad:
						item?.requierePack && item?.unidadPack
							? item.unidadPack
							: item?.tipoUnidad?.nombre || "",
				});
			}
		}

		const input = {
			tipo: "Remito Proveedor",
			idSucursalDestino: idSucursal,
			fechaEntrega: remito.fechaEntrega,
			observaciones: remito.observaciones,
			idProvision: remito.idProvision,
			idProveedor: selectedProvision?.proveedor?.id,
			movimientoDetalle,
		};

		if (cantidadesSuperiores.length > 0) {
			confirmar({
				title: "Cantidades superiores a las esperadas",
				icon: "warning",
				message: <OverdeliveryWarning rows={cantidadesSuperiores} />,
				confirmLabel: "Continuar y guardar",
				cancelLabel: "Seguir editando",
				wide: true,
				func: () => persistRemito(input),
			});
			return;
		}

		persistRemito(input);
	};

	const getEstadoColor = (status) => {
		switch (status) {
			case "Pendiente":
				return "success";
			case "Vencida":
				return "error";
			default:
				return "info";
		}
	};

	const handleSelection = (selected) => {
		setSelectedProvision(selected);
		const provisionCerrada = PROVISION_TERMINAL_STATES.includes(selected?.estado);
		const pedidoIdSel = selected?.pedidoCompra?.id;

		const buildRows = (pedidoDetalles) => {
			const mapDetalles = new Map((pedidoDetalles || []).map(d => [d.id, d]));
			const originalesPedido = (pedidoDetalles || [])
				.map((detalle) => detalle?.materiaPrima)
				.filter(Boolean)
				.reduce((acc, mp) => {
					if (acc.some((curr) => curr.id === mp.id)) return acc;
					acc.push(mp);
					return acc;
				}, []);
			setMateriasPrimaOriginalesPedido(originalesPedido);

			const mpCargados = selected.detalles.map((item) => {
				const pedidoDetalle = mapDetalles.get(item.idPedidoCompraDetalle);
				const cantidadEntregada = Number(item.cantidadEntregada) || 0;
				const cantidadRequerida = Number(item.cantidad) || 0;
				const materiaPrimaCotizada = item.materiaPrima;
				const esAlternativa = materiaPrimaCotizada?.idMateriaPrimaReferencia != null;
				const materiaPrimaDestinoInicial = esAlternativa
					? (cantidadEntregada > 0 ? (item.materiaPrimaRecepcion ?? materiaPrimaCotizada) : null)
					: materiaPrimaCotizada;
				const cantidadPendiente = computePendingQuantity({
					requiredOriginal: cantidadRequerida,
					deliveredOriginal: cantidadEntregada,
				});

				return {
					...materiaPrimaCotizada,
					nombreCompra: nombreCompraDetalle(pedidoDetalle, materiaPrimaCotizada),
					mpOriginal: item.materiaPrima,
					idMateriaPrimaCotizada: materiaPrimaCotizada?.id,
					idMateriaPrimaDestino: materiaPrimaDestinoInicial?.id || null,
					materiaPrimaDestino: materiaPrimaDestinoInicial,
					idProvisionDetalle: item.id,
					cantidad: cantidadPendiente,
					cantidadEntregada,
					cantidadRequerida,
					enabled: !provisionCerrada && cantidadPendiente > 0,
					tipoUnidad: item.materiaPrima?.tipoUnidad,
					precioUnitarioOriginal: item.precioUnitario,
					nuevoPrecio: item.precioUnitario,
					requierePack: pedidoDetalle?.requierePack,
					unidadPack: pedidoDetalle?.unidadPack,
					totalMP: pedidoDetalle?.totalMP,
					cantidadPedidoCompra: pedidoDetalle?.cantidad,
					opcion: 2
				};
			});

			setRemito((prev) => ({
				...prev,
				idProvision: selected.id,
				idPedidoCompra: selected.pedidoCompra?.id,
			}));
			setProductosRemito(mpCargados);
		};

		if (pedidoIdSel) {
			getPedidoCompraDetalles({ variables: { id: pedidoIdSel } })
				.then(({ data }) => {
					const pedidoDetalles = data?.pedidosCompra?.[0]?.detalles || [];
					buildRows(pedidoDetalles);
				})
				.catch(() => {
					// Si falla la carga, construir con coeficiente por defecto (=1)
					buildRows([]);
				});
		} else {
			buildRows([]);
		}
	};

	const handleCambioProducto = ({ selected, data }) => {
		setProductosRemito((prev) => {
			let newProductos = [...prev];
			const current = newProductos[data.index];
			newProductos[data.index] = {
				...current,
				idMateriaPrimaDestino: selected?.id || null,
				materiaPrimaDestino: selected || null,
				idProvisionDetalle: current?.idProvisionDetalle,
				cantidad: current?.cantidad,
				cantidadEntregada: current?.cantidadEntregada,
				cantidadRequerida: current?.cantidadRequerida,
				precioUnitarioOriginal: current?.precioUnitarioOriginal,
				nuevoPrecio: current?.nuevoPrecio,
				mpOriginal: current?.mpOriginal,
			};
			return newProductos;
		});
	};
	const filasSinDestino = productosRemito.filter((item) => !item?.idMateriaPrimaDestino);
	const pendientesDestino = filasSinDestino.filter(
		(item) => item?.idMateriaPrimaReferencia != null
	);
	const tooltipGuardarPendientes = pendientesDestino.length
		? `Falta reemplazar: ${pendientesDestino
			.map((item) => `ID ${item?.id} - ${item?.nombre}`)
			.join(", ")}`
		: "";
	const guardarDisabled =
		loadingSave ||
		PROVISION_TERMINAL_STATES.includes(selectedProvision?.estado) ||
		!remito?.fechaEntrega ||
		productosRemito.filter((i) => i.enabled).length === 0 ||
		filasSinDestino.length > 0 ||
		productosRemito.some(
			(item) =>
				item.enabled &&
				(!item.cantidad ||
					item.cantidad <= 0 ||
					!item.idMateriaPrimaDestino)
		);
	const provisionCerrada = PROVISION_TERMINAL_STATES.includes(selectedProvision?.estado);
	const pedidoId = selectedRemito?.provision?.pedidoCompra?.id || selectedProvision?.pedidoCompra?.id;
	return (
		<>
			<Modal
				aria-labelledby="transition-modal-title"
				aria-describedby="transition-modal-description"
				open={open}
				onClose={handleClose}
				closeAfterTransition
			>
				<Fade in={open}>
					<Card sx={style}>
						<SoftBox display="flex" flexDirection="column" gap={2}>
							<Card>
								<SoftBox p={3} display="flex" flexDirection="column" gap={2}>
									<SoftBox display="flex" justifyContent="space-between" alignItems="center">
										<SoftTypography variant="h6">
											{selectedRemito ? `Recepción n°${selectedRemito.id}` : `Nueva recepción`}
										</SoftTypography>
										<SoftBox>
											<Tooltip title="Cerrar" placement="top">
												<Icon fontSize="medium" onClick={handleClose} sx={{ cursor: "pointer" }}>
													close
												</Icon>
											</Tooltip>
										</SoftBox>
									</SoftBox>

									{selectedProvision ? (
										<SoftBox display="flex" flexDirection="column" gap={2}>
											<SoftBox
												display="flex"
												justifyContent="space-between"
												alignItems={{
													xs: "start",
													sm: "center",
												}}
												flexDirection={{
													xs: "column",
													lg: "row",
												}}
												gap={2}
											>
												<SoftBox alignItems="center">
													<InputLabel htmlFor="orden">
														<SoftTypography component="label" variant="caption" fontWeight="bold">
															Orden de provisión
														</SoftTypography>
													</InputLabel>
													<SoftTypography variant="h6" color="uproPink" fontWeight="bold">
														{`OP #${selectedProvision.id} - ${selectedProvision.proveedor?.razonSocial ??
															`${selectedProvision.proveedor?.nombre} ${selectedProvision.proveedor?.apellido}`
															}`}
													</SoftTypography>
												</SoftBox>
												<SoftBox
													display="flex"
													gap={2}
													alignItems="center"
													flexDirection={{
														xs: "column",
														lg: "row",
													}}
													sx={{
														width: {
															xs: "100%",
															sm: "auto",
														},
													}}
												>
													{!selectedRemito && (
														<SoftButton
															fullWidth
															color="uproYellow"
															circular
															onClick={() => setOpenSelectProvision(true)}
															sx={{
																minWidth: "200px",
															}}
														>
															Cambiar orden&nbsp;<Icon>autorenew</Icon>
														</SoftButton>
													)}
													<Tooltip title="Ver remitos" placement="top">
														<SoftButton
															fullWidth
															color="info"
															circular
															variant="gradient"
															onClick={() => setOpenRemitosModal(true)}
															sx={{
																minWidth: "200px",
															}}
														>
															Ver remitos&nbsp;<Icon>inventory</Icon>
														</SoftButton>
													</Tooltip>
													<Tooltip title="Ver OP" placement="top">
														<SoftButton
															fullWidth
															circular
															color="uproGreen"
															variant="gradient"
															onClick={() => window.open(`/op/pdf/${selectedProvision.id ?? 0}`)}
															sx={{
																minWidth: "120px",
															}}
														>
															Ver OP&nbsp;
															<Icon>file_open</Icon>
														</SoftButton>
													</Tooltip>
													<Tooltip title="Ver SG" placement="top">
														<SoftButton
															fullWidth
															circular
															color="info"
															variant="gradient"
															onClick={() => window.open(`/solicitud/pdf/${pedidoId ?? 0}`)}
														>
															Ver SG&nbsp;
															<Icon>description</Icon>
														</SoftButton>
													</Tooltip>
												</SoftBox>
											</SoftBox>
											<SoftBox
												display="flex"
												justifyContent="space-between"
												alignItems={{
													xs: "start",
													sm: "center",
												}}
												flexDirection={{
													xs: "column",
													lg: "row",
												}}
											>
												<SoftBox>
													<InputLabel htmlFor="orden">
														<SoftTypography component="label" variant="caption" fontWeight="bold">
															Documento
														</SoftTypography>
													</InputLabel>
													<SoftTypography variant="h6" fontWeight="bold">
														{selectedProvision?.proveedor?.documento}
													</SoftTypography>
												</SoftBox>
												<SoftBox>
													<InputLabel htmlFor="fechaVencimiento">
														<SoftTypography component="label" variant="caption" fontWeight="bold">
															Estado
														</SoftTypography>
													</InputLabel>
													<SoftBadge
														variant="contained"
														size="sm"
														badgeContent={selectedProvision.estado}
														color={getEstadoColor(selectedProvision.estado)}
													/>
												</SoftBox>
												<SoftBox>
													<InputLabel htmlFor="fechaVencimiento">
														<SoftTypography component="label" variant="caption" fontWeight="bold">
															Vencimiento
														</SoftTypography>
													</InputLabel>
													<SoftBox display="flex" gap={1} alignItems="center">
														<SoftTypography variant="h6">
															{selectedProvision.fechaVencimiento
																? formatDate(selectedProvision.fechaVencimiento, "dd/MM/yy")
																: "No definida"}
														</SoftTypography>
													</SoftBox>
												</SoftBox>
												<SoftBox>
													<InputLabel htmlFor="fechaVencimiento">
														<SoftTypography component="label" variant="caption" fontWeight="bold">
															Cantidad items
														</SoftTypography>
													</InputLabel>
													<SoftBox display="flex" gap={1} alignItems="center">
														<SoftTypography variant="h6">
															{selectedProvision.detalles?.length}
														</SoftTypography>
													</SoftBox>
												</SoftBox>
												<SoftBox>
													<InputLabel htmlFor="fechaVencimiento">
														<SoftTypography component="label" variant="caption" fontWeight="bold">
															Total
														</SoftTypography>
													</InputLabel>
													<SoftBox display="flex" gap={1} alignItems="center">
														<SoftTypography variant="h6">
															$ {formatMoneyPunto(selectedProvision.total)}
														</SoftTypography>
													</SoftBox>
												</SoftBox>
											</SoftBox>
										</SoftBox>
									) : (
										<SoftBox
											display="flex"
											alignItems="center"
											justifyContent="center"
											flexDirection="column"
											gap={2}
											py={4}
										>
											<SoftTypography variant="caption" color="secondary">
												No hay ninguna orden seleccionada
											</SoftTypography>
											<SoftButton
												color="primary"
												circular
												onClick={() => setOpenSelectProvision(true)}
											>
												Seleccionar orden&nbsp;<Icon>arrow_outward</Icon>
											</SoftButton>
										</SoftBox>
									)}
								</SoftBox>
							</Card>
							<Card>
								<SoftBox display="flex" flexDirection="column" gap={2} p={3}>
									<SoftTypography variant="h6">Información de la recepción</SoftTypography>
									<Grid container spacing={2}>
										<Grid item xs={12} sm={4}>
											<InputLabel htmlFor="sede">
												<SoftTypography component="label" variant="caption" fontWeight="bold">
													Sucursal de destino
												</SoftTypography>
											</InputLabel>
											{selectedRemito ? (
												<SoftTypography variant="h6" fontWeight="bold">
													{`${sucursal?.sucursal?.sede?.nombre} - ${sucursal?.sucursal?.nombre}`}
												</SoftTypography>
											) : sucursal?.sucursal?.nombre ? (
												<SoftInput
													disabled
													value={`${sucursal?.sucursal?.sede?.nombre} - ${sucursal?.sucursal?.nombre}`}
												/>
											) : (
												<SoftTypography variant="h6" fontWeight="bold">
													<Skeleton width={100} />
												</SoftTypography>
											)}
										</Grid>
										<Grid item xs={12} sm={4}>
											<InputLabel htmlFor="fechaEntrega">
												<SoftTypography component="label" variant="caption" fontWeight="bold">
													Fecha de Entrega
													{!selectedRemito && (
														<SoftTypography variant="caption" fontWeight="bold" color="primary">
															&nbsp;*
														</SoftTypography>
													)}
												</SoftTypography>
											</InputLabel>
											{selectedRemito ? (
												<SoftTypography variant="h6" fontWeight="bold">
													{selectedRemito?.fechaEntrega}
												</SoftTypography>
											) : (
												<SoftInput
													label="fechaEntrega"
													type="date"
													name="fechaEntrega"
													value={remito?.fechaEntrega || ""}
													onChange={handleChanges}
												/>
											)}
										</Grid>
										<Grid item xs={12}>
											<InputLabel htmlFor="observaciones">
												<SoftTypography component="label" variant="caption" fontWeight="bold">
													Observaciones
												</SoftTypography>
											</InputLabel>
											{selectedRemito ? (
												<SoftTypography variant="h6" fontWeight="bold">
													{selectedRemito?.observaciones}
												</SoftTypography>
											) : (
												<SoftInput
													label="observaciones"
													placeholder="Inserte un comentario"
													type="text"
													name="observaciones"
													value={remito?.observaciones || ""}
													onChange={handleChanges}
													multiline
													rows={1}
												/>
											)}
										</Grid>
									</Grid>
								</SoftBox>
							</Card>

							<Card>
								<SoftBox
									display="flex"
									justifyContent="space-between"
									alignItems="center"
									p={3}
									pb={0}
								>
									<SoftBox>
										<SoftTypography variant="h6">Productos</SoftTypography>
									</SoftBox>
									{selectedRemito && (
										<SoftBox display="flex" justifyContent="end" alignItems="center" gap={1}>
											<SoftTypography variant="caption">
												Mostrar solo de este movimiento
											</SoftTypography>
											<Switch
												checked={filterQuantity}
												onChange={() => setFilterQuantity(!filterQuantity)}
											/>
										</SoftBox>
									)}
								</SoftBox>
								<SoftBox
									sx={{
										"& .MuiTableRow-root:not(:last-child)": {
											"& td": {
												borderBottom: ({ borders: { borderWidth, borderColor } }) =>
													`${borderWidth[1]} solid ${borderColor}`,
											},
										},
									}}
								>
									{(productosRemito || []).length > 0 ? (
										<TableContainer
											sx={{
												width: "100%",
												overflowX: "auto",
												overflowY: "hidden",
												WebkitOverflowScrolling: "touch",
											}}
										>
											<Table
												aria-label="Productos de la recepción"
												sx={{
													width: "100%",
													tableLayout: "auto",
                          "@media (max-width:1199px)": {
                            display: "block",
                            "& thead": { display: "none" },
                            "& tbody": { display: "block" },
                            "& tr": { display: "grid", gridTemplateColumns: "repeat(2,minmax(0,1fr))", gap: 1, border: "1px solid #E7EBF3", borderRadius: 2, mb: 2 },
                            "& td": { display: "block", minWidth: 0, whiteSpace: "normal" },
                            "& td:nth-of-type(2)": { gridColumn: "1 / -1" },
                            "& td::before": { content: "attr(data-label)", display: "block", fontSize: "10px", fontWeight: 700, color: "#67748e", textAlign: "left" },
                          },
													"& th, & td": {
														whiteSpace: "nowrap",
													},
												}}
											>
												<SoftBox component="thead">
													{selectedRemito ? (
														<TableRow>{renderColumnsSelect}</TableRow>
													) : (
														<TableRow>{renderColumnsBase}</TableRow>
													)}
												</SoftBox>
												<TableBody>
													{(productosRemito || [])
														.filter((i) => !filterQuantity || i.cantidad > 0)
														.map((item, index) => {
															return (
																<TableItem
																			key={`${item.id}-${item.idProvisionDetalle ?? index}`}
																			item={item}
																			handleUpdate={(updated) => handleUpdateItem(updated, index)}
																			disabled={Boolean(selectedRemito) || provisionCerrada}
																	showDestinoColumn={!selectedRemito}
																	cambiarProducto={(data) => {
																		setCambioProductoData({ index, ...data });
																	}}
																/>
															);
														})}
												</TableBody>
											</Table>
										</TableContainer>
									) : (
										<SinDatos />
									)}
								</SoftBox>
							</Card>

							{!selectedRemito && (
								<Card>
									<SoftBox
										display="flex"
										justifyContent="end"
										alignItems="center"
										p={2}
										flexDirection={{
											xs: "column",
											lg: "row",
										}}
										gap={2}
									>
										<SoftBox
											sx={{
												width: {
													xs: "100%",
													sm: "auto",
												},
											}}
										>
											<SoftButton color="primary" circular onClick={handleClose} fullWidth>
												<Icon sx={{ fontWeight: "light" }}>cancel</Icon>
												&nbsp;Cancelar
											</SoftButton>
											</SoftBox>
											<SoftBox
												sx={{
													width: {
														xs: "100%",
														sm: "auto",
													},
												}}
											>
												<Tooltip
													title={tooltipGuardarPendientes}
													placement="top"
													disableHoverListener={!tooltipGuardarPendientes}
												>
													<span>
														<SoftButton
															color="uproGreen"
															circular
															fullWidth
															disabled={guardarDisabled}
															onClick={handleSave}
														>
															{loadingSave ? (
																<CircularProgress size={15} color="white" />
															) : (
																<Icon sx={{ fontWeight: "light" }}>save</Icon>
															)}
															&nbsp;Guardar
														</SoftButton>
													</span>
												</Tooltip>
											</SoftBox>
									</SoftBox>
								</Card>
							)}
						</SoftBox>
						<ModalProducto
							id={selectedItem?.id || null}
							tipo={selectedItem?.tipo || null}
							open={openModalProducto}
							handleClose={() => {
								setOpenModalProducto(false);
								handleCloseMenu();
								handleCloseAddMenu();
							}}
							refetch={() => { }}
							onCreate={(newId) => {
								getProductos({
									variables: {
										page: 1,
										filter: {
											disabled: false,
											id: newId,
										},
									},
								});
								setProductoToAdd(newId);
							}}
						/>

						<ModalCambioProducto
							data={cambioProductoData}
							handleClose={() => setCambioProductoData(null)}
							setSelection={handleCambioProducto}
							defaultOptions={materiasPrimaOriginalesPedido}
						/>

						<ModalSelectProvision
							open={openSelectProvision}
							handleClose={() => setOpenSelectProvision(false)}
							setSelection={handleSelection}
						/>
					</Card>
				</Fade>
			</Modal>

			<ModalRemitos
				open={openRemitosModal}
				handleClose={() => setOpenRemitosModal(false)}
				idProvision={selectedProvision?.id || null}
				provisionData={selectedProvision}
			/>
		</>
	);
};

ModalNuevoRemito.propTypes = {
	open: PropTypes.bool,
	handleClose: PropTypes.func,
	selectedRemito: PropTypes.object,
	idSucursal: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	refetchProductos: PropTypes.func,
};

